// Cookie / tracking consent — KVKK (Türkiye) + GDPR (EU) compliant.
//
// Legal model: no non-essential tracking (PostHog, first-party analytics,
// marketing pixels) may run until the visitor has made an explicit, granular
// choice. "Strictly necessary" storage (auth token, language, this consent
// record itself) is always allowed — it is exempt under both regimes.
//
// Consent is stored client-side only. Bumping CONSENT_VERSION invalidates every
// stored decision and re-prompts everyone (use when the categories or the
// vendors behind them materially change).

export type ConsentCategory = 'necessary' | 'analytics' | 'marketing'

export interface ConsentState {
  v: number
  ts: number // epoch ms of the decision
  analytics: boolean
  marketing: boolean
}

const STORAGE_KEY = 'oaas_cookie_consent'
export const CONSENT_VERSION = 1
const CHANGE_EVENT = 'oaas-consent-changed'
const OPEN_SETTINGS_EVENT = 'oaas-open-cookie-settings'

// "necessary" is implicit and always granted — never stored as a choice.
const DEFAULT_DENIED: Omit<ConsentState, 'v' | 'ts'> = {
  analytics: false,
  marketing: false,
}

function read(): ConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ConsentState
    // A decision from an older policy version no longer counts — re-prompt.
    if (parsed.v !== CONSENT_VERSION) return null
    return parsed
  } catch {
    return null
  }
}

/** Has the visitor made an explicit choice under the current policy version? */
export function hasDecided(): boolean {
  return read() !== null
}

/** Current stored decision, or the safe all-denied default before any choice. */
export function getConsent(): Omit<ConsentState, 'v' | 'ts'> {
  const stored = read()
  if (!stored) return { ...DEFAULT_DENIED }
  return { analytics: stored.analytics, marketing: stored.marketing }
}

/** Is a given category currently permitted? "necessary" is always true. */
export function hasConsent(category: ConsentCategory): boolean {
  if (category === 'necessary') return true
  return getConsent()[category]
}

/**
 * Persist a decision and notify listeners. Called from the banner / preferences
 * panel. Every path (accept-all, reject, save-custom) funnels through here.
 */
export function setConsent(prefs: Omit<ConsentState, 'v' | 'ts'>): void {
  const state: ConsentState = {
    v: CONSENT_VERSION,
    ts: Date.now(),
    analytics: !!prefs.analytics,
    marketing: !!prefs.marketing,
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    // Withdrawal must actually stop the processing, not just future events:
    // drop the first-party tracker's stored identifiers so nothing usable
    // stays behind on the device. (Keys owned by lib/tracker.ts.)
    if (!state.analytics) {
      localStorage.removeItem('_oaas_vid')
      sessionStorage.removeItem('_oaas_sid')
      sessionStorage.removeItem('_oaas_utm')
    }
  } catch {
    // If storage is unavailable, consent simply won't persist — the safe
    // default (denied) holds and the banner re-shows next load.
  }
  emitChange()
}

/** Withdraw all non-essential consent (GDPR/KVKK: withdrawal must be as easy as granting). */
export function withdrawConsent(): void {
  setConsent({ ...DEFAULT_DENIED })
}

function emitChange(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
}

/** Subscribe to consent changes. Returns an unsubscribe fn. */
export function onConsentChange(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(CHANGE_EVENT, cb)
  return () => window.removeEventListener(CHANGE_EVENT, cb)
}

// --- Reopening the preferences panel (withdrawal / change of mind) ----------
// The banner unmounts after a decision, so anything else (footer link, the
// floating cookie button) asks it to reopen via this event.

export function openCookieSettings(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(OPEN_SETTINGS_EVENT))
}

export function onOpenCookieSettings(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(OPEN_SETTINGS_EVENT, cb)
  return () => window.removeEventListener(OPEN_SETTINGS_EVENT, cb)
}
